import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-select';
import AppConst from '../apis/appconst';

/**
 * AddServiceGroup — Modal for creating/editing a service group.
 *
 * Props:
 *   - item: the group being edited (or null for new)
 *   - allServices: array of all services from cat_tbl ({cat_id, cat_name, price})
 *   - onSaved: callback after successful save (parent re-fetches list)
 *
 * Bootstrap modal triggered by an invisible button with id="addgrpbt789".
 */
function AddServiceGroup({ item, allServices, onSaved }) {
    const [groupName, setGroupName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const isEdit = item && item.cat_id;

    // Build service options for the select
    const serviceOptions = useMemo(() => {
        return (allServices || []).map(s => ({
            value: s.cat_id,
            label: s.cat_name + (s.price ? ` (₹${s.price})` : '')
        }));
    }, [allServices]);

    // When item prop changes, prefill the form
    useEffect(() => {
        if (isEdit) {
            setGroupName(item.cat_name || '');
            setDescription(item.cat_type2 || '');

            // Prefill currently linked services
            const linkedIds = (item.services || []).map(s => s.cat_id);
            setSelectedServices(
                serviceOptions.filter(o => linkedIds.includes(o.value))
            );
        } else {
            setGroupName('');
            setDescription('');
            setSelectedServices([]);
        }
    }, [item, serviceOptions, isEdit]);

    const handleSave = async () => {
        if (!groupName.trim()) {
            toast.error('Group name is required');
            return;
        }

        setIsSaving(true);
        try {
            const fd = new FormData();
            if (isEdit) fd.append('cat_id', item.cat_id);
            fd.append('cat_name', groupName.trim());
            fd.append('cat_type2', description.trim());
            fd.append('service_ids', JSON.stringify(selectedServices.map(s => s.value)));

            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.saveServiceGroupUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (resp?.data?.status) {
                toast.success(resp.data.success || 'Saved');
                // Close the modal
                const closeBtn = document.getElementById('closeGroupModal');
                if (closeBtn) closeBtn.click();
                if (typeof onSaved === 'function') onSaved();
            } else {
                toast.error(resp?.data?.success || 'Failed to save group');
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to save group');
        } finally {
            setIsSaving(false);
        }
    };

    const reactSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            fontSize: '14px',
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: '150px',
            overflowY: 'auto',   // scroll if many services picked
        }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e7f1ff', borderRadius: '3px' }),
        multiValueLabel: (base) => ({ ...base, color: '#0d6efd', fontSize: '12px' }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuList: (base) => ({
            ...base,
            maxHeight: '220px',
            overflowY: 'auto',       // scroll the dropdown list of services
            paddingBottom: '12px',   // breathing room for the last item
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }), // render above modal
    };

    return (
        <>
            {/* Hidden trigger — parent calls click() to open */}
            <button
                id="addgrpbt789"
                type="button"
                style={{ display: 'none' }}
                data-bs-toggle="modal"
                data-bs-target="#serviceGroupModal"
            />

            <div
                className="modal fade"
                id="serviceGroupModal"
                tabIndex="-1"
                aria-labelledby="serviceGroupModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="serviceGroupModalLabel">
                                <i className="ri-folder-2-line me-2"></i>
                                {isEdit ? 'Edit Service Group' : 'Add Service Group'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="closeGroupModal"
                            />
                        </div>

                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '80px' }}>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label fw-bold">
                                        Group Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Hospital, OT, Lab Services"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label">Description (optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Short description of this group"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label fw-bold">
                                        Select Services <span className="text-muted small">({selectedServices.length} selected)</span>
                                    </label>
                                    <Select
                                        isMulti
                                        options={serviceOptions}
                                        value={selectedServices}
                                        onChange={(s) => setSelectedServices(s || [])}
                                        placeholder="Choose services to include in this group..."
                                        isClearable
                                        isSearchable
                                        styles={reactSelectStyles}
                                        closeMenuOnSelect={false}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                        maxMenuHeight={220}
                                        menuShouldScrollIntoView={false}
                                    />
                                    <small className="text-muted">
                                        Services already assigned to another group will be moved to this one.
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
                                ) : (
                                    <><i className="ri-save-line me-1"></i>{isEdit ? 'Update Group' : 'Create Group'}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddServiceGroup;